import { Line, Divider } from "./fields";
import { StatusPill } from "@/components/lux";
import { money } from "@/dealership/lib/desk";

/** Sticky right-hand column: the deal build-up, the payment, and internal gross. */
export default function SummaryPanel({ ws, totals, internal }) {
  const cash = totals.mode === "cash";
  return (
    <div className="space-y-4" data-testid="desk-summary">
      <div className="border border-border bg-card p-5">
        <div className="label-mono">Out-the-door</div>
        <div className="font-heading text-3xl money tnum mt-1" data-testid="desk-otd">{money(totals.out_the_door)}</div>
        <Divider />
        <Line label="Net selling price" value={totals.selling_price_net} muted />
        {totals.fees_total > 0 && <Line label="Fees" value={totals.fees_total} muted />}
        {totals.products_total > 0 && <Line label="Products" value={totals.products_total} muted />}
        {totals.luxury_tax > 0 && <Line label="Luxury tax" value={totals.luxury_tax} muted />}
        <Line label={totals.tax_exempt ? "HST (exempt)" : `HST ${totals.tax_rate}%`} value={totals.tax_exempt ? "No tax" : totals.tax} muted />
        {totals.rebate_after_tax > 0 && <Line label="Rebate / credit" value={-totals.rebate_after_tax} muted negative />}
        {ws.has_trade && (
          <>
            <Line label="Less trade allowance" value={-totals.trade_allowance} muted negative />
            {totals.trade_lien > 0 && <Line label="Plus lien payout" value={totals.trade_lien} muted />}
          </>
        )}
        {totals.deposit > 0 && <Line label="Less deposit" value={-totals.deposit} muted negative />}
        {totals.cash_down > 0 && <Line label="Less cash down" value={-totals.cash_down} muted negative />}
        {totals.products_upfront > 0 && <Line label="Less products paid upfront" value={-totals.products_upfront} muted negative />}
        <Divider />
        {cash ? (
          <Line label="Balance due on delivery" value={totals.cash_due} bold gold testid="desk-cash-due" />
        ) : (
          <Line label="Amount financed" value={totals.amount_financed} bold gold testid="desk-amount-financed" />
        )}
        {totals.cash_back > 0 && <Line label="Refund to customer" value={totals.cash_back} muted />}
        {totals.due_at_delivery > 0 && <Line label="Due at delivery" value={totals.due_at_delivery} muted />}
      </div>

      {!cash && (
        <div className="border border-primary/30 bg-primary/[0.05] p-5" data-testid="desk-payment-card">
          <div className="label-mono">{totals.frequency_label} payment</div>
          <div className="font-heading text-5xl money tnum mt-1" data-testid="desk-payment">{money(totals.payment)}</div>
          <div className="text-xs text-muted-foreground mt-2">
            {totals.n_payments} payments · {totals.apr}% APR · {totals.term_months} months
          </div>
          <Divider />
          {totals.deferral_interest > 0 && <Line label={`Interest for ${totals.deferral_days} deferred days`} value={totals.deferral_interest} muted />}
          <Line label="Total of payments" value={totals.total_of_payments} muted testid="desk-total-payments" />
          <Line label="Cost of borrowing" value={totals.cost_of_borrowing} muted testid="desk-cost-borrowing" />
          {totals.final_payment !== totals.payment && <Line label="Final payment" value={totals.final_payment} muted />}
          {ws.finance?.first_payment_date && <Line label="First payment" value={new Date(ws.finance.first_payment_date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })} muted />}
        </div>
      )}

      {internal && (
        <div className="border border-border bg-card p-5" data-testid="desk-gross-strip">
          <div className="flex items-center justify-between gap-2">
            <div className="label-mono">Gross</div>
            <StatusPill status="internal" tone="red">Internal only</StatusPill>
          </div>
          <Line label="Front-end" value={totals.front_gross} />
          <Line label="Back-end" value={totals.back_gross} />
          {totals.finance_reserve > 0 && <Line label="incl. reserve" value={totals.finance_reserve} muted indent />}
          <Divider />
          <Line label="Total deal gross" value={totals.total_gross} bold gold testid="desk-total-gross" />
          <div className="text-[11px] text-muted-foreground mt-2">
            Cost basis {money(totals.cost_basis)} + pack {money(totals.pack)}
            {totals.over_allowance > 0 ? ` + over-allowance ${money(totals.over_allowance)}` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
